import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material'
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined'
import { Link as RouterLink } from 'react-router-dom'
import { useProjects } from '../api/hooks'
import { formatEUR2, formatPct } from '../format'
import { statusLabels, type ProjectStatus } from '../api/types'
import { AMOUNT_MASK, useAmountsHidden } from '../privacy'
import SharedBadge from '../components/SharedBadge'
import { Stat } from '../components/Stat'
import StatCard from '../components/StatCard'

const statusColors: Record<ProjectStatus, string> = {
  negotiating: '#E0A96D',
  in_progress: '#7FBF9E',
  archived: '#D8B8A8',
}

export default function DashboardPage() {
  const projects = useProjects()
  const projList = projects.data ?? []
  const isMobileView = useMediaQuery((t) => t.breakpoints.down('sm'))

  const hidden = useAmountsHidden()

  const rentalProjects = projList.filter((p) => p.simulation && p.simulation.monthlyRent > 0)
  const avgYield =
    rentalProjects.length > 0
      ? rentalProjects.reduce((s, p) => s + (p.simulation?.results.netYield ?? 0), 0) /
        rentalProjects.length
      : 0
  const cumulatedCashFlow = rentalProjects.reduce(
    (s, p) => s + (p.simulation?.results.monthlyCashFlow ?? 0),
    0,
  )

  const statusCounts = projList.reduce(
    (acc, p) => {
      acc[p.status] = (acc[p.status] ?? 0) + 1
      return acc
    },
    {} as Partial<Record<ProjectStatus, number>>,
  )
  const statusOrder = Object.keys(statusLabels) as ProjectStatus[]
  const donutSegments = statusOrder
    .map((status) => ({ status, count: statusCounts[status] ?? 0 }))
    .filter((s) => s.count > 0)

  const RING_SIZE = 160
  const RING_STROKE = 12
  const RING_GAP = 4
  const ringRadius = (RING_SIZE - RING_STROKE) / 2
  const ringCircumference = 2 * Math.PI * ringRadius
  const ringSegments = donutSegments.reduce<
    { status: ProjectStatus; length: number; offset: number }[]
  >((acc, seg) => {
    const prev = acc[acc.length - 1]
    const start = prev ? prev.offset + prev.length : 0
    const arc = (seg.count / projList.length) * ringCircumference
    acc.push({ status: seg.status, length: arc, offset: start })
    return acc
  }, [])
  const ringSingle = ringSegments.length === 1
  // round caps extend by stroke/2 on each side: shrink arc so visible gap = RING_GAP
  const capPad = RING_STROKE / 2 + RING_GAP / 2

  type StatData = {
    key: string
    label: string
    value: string
    tone?: 'positive' | 'negative'
  }

  const stats: StatData[] = [
    {
      key: 'patrimoine',
      label: 'Patrimoine simulé',
      value: `${projList.length} projets`,
    },
    {
      key: 'rendement',
      label: 'Rendement net moyen',
      value: formatPct(avgYield),
    },
    {
      key: 'cashflow',
      label: 'Cash-flow cumulé',
      value: `${formatEUR2(cumulatedCashFlow)}/mois`,
      tone: cumulatedCashFlow >= 0 ? 'positive' : 'negative',
    },
  ]

  return (
    <Box>
      <Stack
        direction="row"
        sx={{ mb: 4, mt: { xs: 0, md: 4 }, justifyContent: 'space-between', alignItems: 'center' }}
      >
        <Typography variant="h4">Tableau de bord</Typography>
      </Stack>

      <Box sx={{ mb: 4 }}>
        {!isMobileView ? (
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: 'repeat(3, 1fr)',
            }}
          >
            {stats.map((s) => (
              <StatCard key={s.key} label={s.label} value={s.value} tone={s.tone} />
            ))}
          </Box>
        ) : (
          <Card>
            <Stack divider={<Divider />}>
              {stats.map((s) => (
                <Stat key={s.key} label={s.label} value={s.value} tone={s.tone} inline />
              ))}
            </Stack>
          </Card>
        )}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: { xs: '1fr', md: '320px 1fr' },
        }}
      >
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Statut des projets
            </Typography>
            <Box
              sx={{
                position: 'relative',
                width: RING_SIZE,
                height: RING_SIZE,
                mx: 'auto',
              }}
            >
              <svg
                width={RING_SIZE}
                height={RING_SIZE}
                viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
                style={{ transform: 'rotate(-90deg)' }}
                aria-hidden
              >
                <circle
                  cx={RING_SIZE / 2}
                  cy={RING_SIZE / 2}
                  r={ringRadius}
                  fill="none"
                  stroke="rgba(255,255,255,0.06)"
                  strokeWidth={RING_STROKE}
                  strokeDasharray={projList.length === 0 ? '4 8' : undefined}
                  strokeLinecap={projList.length === 0 ? 'round' : undefined}
                />
                {ringSegments.map((seg) => {
                  const len = ringSingle
                    ? ringCircumference
                    : Math.max(seg.length - 2 * capPad, 0.01)
                  const offset = ringSingle ? 0 : seg.offset + capPad
                  return (
                    <circle
                      key={seg.status}
                      cx={RING_SIZE / 2}
                      cy={RING_SIZE / 2}
                      r={ringRadius}
                      fill="none"
                      stroke={statusColors[seg.status]}
                      strokeWidth={RING_STROKE}
                      strokeLinecap={ringSingle ? 'butt' : 'round'}
                      strokeDasharray={`${len} ${ringCircumference - len}`}
                      strokeDashoffset={-offset}
                    />
                  )
                })}
              </svg>
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="h3" sx={{ lineHeight: 1 }}>
                  {projList.length}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5 }}>
                  {projList.length > 1 ? 'projets' : 'projet'}
                </Typography>
              </Box>
            </Box>
            <Stack spacing={1} sx={{ mt: 3 }}>
              {donutSegments.map((seg) => (
                <Stack key={seg.status} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: statusColors[seg.status],
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {statusLabels[seg.status]} · {seg.count}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Projets récents
            </Typography>
            {projList.length === 0 && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Aucun projet. Créez-en un depuis une simulation.
              </Typography>
            )}
            <Stack spacing={1.5}>
              {projList.slice(0, 5).map((p) => {
                const r = p.simulation?.results
                return (
                  <Box
                    key={p.id}
                    component={RouterLink}
                    to={`/projects/${p.id}`}
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'stretch', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                      textDecoration: 'none',
                      color: 'inherit',
                      p: 1.5,
                      borderRadius: 2,
                      '&:hover': { bgcolor: 'rgba(245,245,245,0.06)' },
                    }}
                  >
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        flexShrink: 0,
                        bgcolor: 'action.hover',
                        border: '1px dashed',
                        borderColor: 'divider',
                        display: { xs: 'none', sm: 'flex' },
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <AddPhotoAlternateOutlinedIcon sx={{ color: 'text.secondary' }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography noWrap sx={{ fontWeight: 600 }}>
                        {p.name}
                      </Typography>
                      <Typography variant="caption" noWrap sx={{ color: 'text.secondary' }}>
                        {p.simulation?.address || '—'}
                      </Typography>
                    </Box>
                    <Stack
                      spacing={0.25}
                      sx={{ flexShrink: 0, alignItems: { xs: 'flex-start', sm: 'flex-end' } }}
                    >
                      {r && (
                        <>
                          {p.simulation?.monthlyRent ? (
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color: hidden
                                  ? 'text.primary'
                                  : r.monthlyCashFlow >= 0
                                    ? 'success.main'
                                    : 'error.main',
                              }}
                            >
                              Loc. {formatEUR2(r.monthlyCashFlow)}/mois
                            </Typography>
                          ) : p.simulation?.pricePerM2Resale ? (
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color: hidden
                                  ? 'text.primary'
                                  : r.netMarginChargePercent >= 0
                                    ? 'success.main'
                                    : 'error.main',
                              }}
                            >
                              Revente {hidden ? AMOUNT_MASK : formatPct(r.netMarginChargePercent)}
                            </Typography>
                          ) : null}
                        </>
                      )}
                      <SharedBadge
                        role={p.role}
                        ownerEmail={p.ownerEmail}
                        sharedWithOthers={p.sharedWithOthers}
                      />
                      <Chip
                        size="small"
                        label={statusLabels[p.status]}
                        sx={{
                          mt: 0.5,
                          bgcolor: statusColors[p.status],
                          color: 'background.default',
                        }}
                      />
                    </Stack>
                  </Box>
                )
              })}
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </Box>
  )
}
